import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PasswordModal } from '../components/case-study/PasswordModal';
import { matchesCaseStudyPassword } from '../utils/caseStudyLock';
import { useScreenInit } from '../useScreenInit.js';
type CaseStudyAccessValue = {
  isUnlocked: boolean;
  verify: (password: string) => boolean;
  grantAccess: () => void;
  requestAccess: (target: string) => void;
};
const STORAGE_KEY = 'kd-case-study-access';
const CaseStudyAccessContext = createContext<CaseStudyAccessValue | null>(null);
export function CaseStudyAccessProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const screenInit = useScreenInit() as {
    unlocked?: boolean;
  };
  const [isUnlocked, setIsUnlocked] = useState(() => typeof screenInit.unlocked === 'boolean' ? screenInit.unlocked : readStoredAccess());
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);
  const verify = useCallback((password: string) => matchesCaseStudyPassword(password), []);
  const grantAccess = useCallback(() => {
    setIsUnlocked(true);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, 'granted');
    } catch {
      // Storage unavailable (e.g. private mode); access lasts for this page session only.
    }
  }, []);
  const requestAccess = useCallback((target: string) => {
    if (isUnlocked) {
      navigate(target);
      return;
    }
    setPendingTarget(target);
  }, [isUnlocked, navigate]);
  const value = useMemo(() => ({
    isUnlocked,
    verify,
    grantAccess,
    requestAccess
  }), [isUnlocked, verify, grantAccess, requestAccess]);
  return <CaseStudyAccessContext.Provider value={value} data-id="element-785" data-mp="1b8dd334b06no">
      {children}
      <PasswordModal open={pendingTarget !== null} onClose={() => setPendingTarget(null)} verify={verify} onSuccess={() => {
      grantAccess();
      const target = pendingTarget;
      setPendingTarget(null);
      if (target) navigate(target);
    }} data-id="element-786" data-mp="2ho5w1v8v1e8x" />
    </CaseStudyAccessContext.Provider>;
}
export function useCaseStudyAccess() {
  const ctx = useContext(CaseStudyAccessContext);
  if (!ctx) throw new Error('useCaseStudyAccess must be used within CaseStudyAccessProvider');
  return ctx;
}
function readStoredAccess() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'granted';
  } catch {
    return false;
  }
}